// TODO(969c-followup): this is the THIRD caller of GET /api/Master/DiagnosisCodes.
// The other two are hooks — `@/patients/services/use-diagnosis-code-search` and its
// boundary-respecting copy `@/billing/services/use-claim-diagnosis`, whose own TODO already
// asks for a single `@/shared/` ICD-10 search. This file does not consolidate them, and adding
// a caller without consolidating makes the drift worse, so: stated plainly rather than left to
// be discovered. When that shared hook lands, delete this and call it imperatively.
//
// Why this exists as a FUNCTION and not a hook: the accept path is a one-shot response to a
// click, not a render-driven typeahead. Expressing it as `useQuery` would mean setting a
// "pending accept" into state, waiting for a render, reading `data` in an effect and then
// dispatching — a race with its own cache-hit and re-render edge cases, on the code path that
// decides what goes into a patient's assessment. A promise the handler can await has none of
// that.
import { get } from '@/shared/api/api-client';
import type { ConditionCode } from '@/types/patient-chart-api';
import type { ConceptRef } from './accept-rules';

/** Matches the hooks above so a resolve shares their cache entry rather than refetching. */
const SYSTEM = 'ICD-10';

/**
 * `GET /api/Master/DiagnosisCodes` returns rows whose `code` field is itself a Coding object
 * carrying PascalCase `System`/`Definition` — NOT the flat `{ code, display }` that
 * `ConceptRef` and the assessment wire body expect.
 *
 * Storing a raw row verbatim is a known 400: it double-nests to `code.code: <object>` and the
 * backend rejects it. `flattenMasterCodeRow` in `patients/components/chart/add-problem-editor.tsx`
 * documents that failure. This is the same flattening, kept here because `decideAccept`
 * explicitly takes the flattened shape and says reconciling wire quirks is the caller's job.
 */
function flatten(row: ConditionCode): ConceptRef | null {
  // The row's `code` is the real Coding. Guard it: a row without one cannot identify a
  // diagnosis, and passing it through would hand `decideAccept` a concept whose `code` is
  // undefined — which its `sameCode` check would then reject anyway, but only after the
  // proposal had been counted as "resolved".
  const coding = (row as { code?: { code?: string; display?: string; System?: string | null } })
    .code;
  if (!coding?.code) return null;
  return {
    code: coding.code,
    display: coding.display ?? coding.code,
    system: coding.System ?? SYSTEM,
  };
}

/**
 * Resolves a proposal's ICD-10 code against Master, returning the flattened candidates for
 * `AcceptContext.resolved`.
 *
 * Searches by CODE, not by display text. Master's search is a substring match over
 * `Code + " " + Display + " " + Definition`, so searching the model's wording ("Headache")
 * returns every row mentioning it and `decideAccept` would never see the single exact match
 * that lets it auto-add. Searching the code narrows to the rows that could actually match —
 * and `decideAccept` still requires exactly one result whose code is EQUAL, so a substring
 * hit on an unrelated row's definition cannot be mistaken for a resolution (PROD-1949).
 *
 * Returns `[]` when there is no code to resolve. That is the normal path, not an error: it
 * makes `decideAccept` fall through to a seeded search, which is what should happen when the
 * model proposed no code.
 */
export async function resolveDiagnosisCode(
  icdCode: string | null | undefined,
): Promise<ConceptRef[]> {
  const trimmed = icdCode?.trim();
  if (!trimmed) return [];

  // A plain request, NOT queryClient.fetchQuery.
  //
  // The first version took a QueryClient so a resolve could share a cache entry with the
  // typeahead. That meant `useQueryClient()` in EncounterShellPage, which broke all 191
  // shell-page tests at once: they mock the service hooks, so none of them wrap the page in a
  // QueryClientProvider, and an unmocked `useQueryClient()` throws. Caught by running them.
  //
  // Sharing one cache entry was never worth much — this fires on a click, not on every
  // keystroke — and paying for it with a provider dependency in the page component is a bad
  // trade. One request per accept is fine.
  const qs = new URLSearchParams({ system: SYSTEM, searchQuery: trimmed });
  const rows = await get<ConditionCode[]>(`/api/Master/DiagnosisCodes?${qs.toString()}`);

  return (rows ?? []).map(flatten).filter((c): c is ConceptRef => c !== null);
}
